import React, { useState } from "react";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { formatError } from "../lib/api";
import { useAuth } from "../lib/auth";

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 pr-10 focus:outline-none focus:border-red-500 text-sm";

function PwField({ label, value, onChange, testid, show, autoComplete }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        data-testid={testid}
        className={`${inputCls} mt-1`}
      />
    </label>
  );
}

export const ChangePasswordForm = ({ currentLabel = "Current password", onDone, idPrefix = "change-pw" }) => {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) return setError("New password must be at least 6 characters");
    if (next !== confirm) return setError("New passwords don't match");
    setSaving(true);
    try {
      await changePassword(current, next);
      setCurrent(""); setNext(""); setConfirm("");
      onDone?.();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${idPrefix}-form`}>
      <PwField label={currentLabel} value={current} onChange={setCurrent} show={show}
        testid={`${idPrefix}-current`} autoComplete="current-password" />
      <div className="grid md:grid-cols-2 gap-4">
        <PwField label="New password" value={next} onChange={setNext} show={show}
          testid={`${idPrefix}-new`} autoComplete="new-password" />
        <PwField label="Confirm new password" value={confirm} onChange={setConfirm} show={show}
          testid={`${idPrefix}-confirm`} autoComplete="new-password" />
      </div>
      {error && (
        <div data-testid={`${idPrefix}-error`} className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${idPrefix}-toggle-visibility`}
          className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5 transition-colors">
          {show ? <EyeOff size={12} /> : <Eye size={12} />} {show ? "Hide" : "Show"} passwords
        </button>
        <button type="submit" disabled={saving || !current || !next || !confirm}
          data-testid={`${idPrefix}-submit`} className="btn-liquid disabled:opacity-50">
          <KeyRound size={14} /> {saving ? "Updating..." : "Update password"}
        </button>
      </div>
    </form>
  );
};
